// ────────────────────────────────────────────────────────────────────────────
//  useRecruitment.ts — Stellen-Links + Bewerbungen (Recruitment-Extension)
//
//  Worker-Endpunkte:
//    GET    /recruitment/links            — Stellen
//    POST   /recruitment/links            — neu
//    PUT    /recruitment/links/:id        — update
//    DELETE /recruitment/links/:id        — löschen
//    GET    /recruitment/applications     — Bewerbungen
//    GET    /recruitment/applications/:id — Detail
//    PUT    /recruitment/applications/:id — Status/Notizen ändern
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback } from 'react'
import { api, API_ORIGIN } from './api'

export interface JobLink {
  id:               number
  orgId:            number
  orgName:          string | null
  code:             string
  title:            string
  description:      string | null
  positionId:       number | null
  positionTitle:    string | null
  createdByUserId:  string
  createdByName:    string | null
  createdAt:        string
  expiresAt:        string | null
  isActive:         boolean
  usedCount:        number
  publicUrl:        string
}

export interface JobApplication {
  id:               number
  linkId:           number | null
  linkTitle:        string | null
  orgId:            number
  firstName:        string
  lastName:         string
  email:            string
  phone:            string | null
  coverLetter:      string | null
  status:           'New' | 'Viewed' | 'Contacted' | 'Accepted' | 'Rejected' | string
  internalNotes:    string | null
  assignedToUserId: string | null
  assignedToName:   string | null
  applicantUserId:  string | null
  submittedAt:      string
  firstViewedAt:    string | null
  lastUpdatedAt:    string | null
  attachments:      Array<{ url: string; name: string; type: string; bytes: number }>
}

// ── Stellen-Links ──────────────────────────────────────────────────────────
export function useJobLinks() {
  const [items, setItems] = useState<JobLink[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get<JobLink[] | { items: JobLink[] }>('/recruitment/links')
      const arr = Array.isArray(r) ? r : (r?.items ?? [])
      setItems(arr)
      setError(null)
    } catch (e: any) {
      setError(e?.message ?? 'load failed')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  return { items, loading, error, refresh }
}

export async function createJobLink(opts: {
  title: string; description?: string; positionId?: number | null; expiresAt?: string | null
}): Promise<JobLink> {
  return api.post<JobLink>('/recruitment/links', opts)
}

export async function updateJobLink(id: number, opts: {
  title?: string; description?: string; isActive?: boolean; expiresAt?: string | null
}): Promise<JobLink> {
  return api.put<JobLink>(`/recruitment/links/${id}`, opts)
}

export async function deleteJobLink(id: number): Promise<void> {
  await api.delete(`/recruitment/links/${id}`)
}

// ── Bewerbungen ────────────────────────────────────────────────────────────
export function useApplications(filterStatus?: string) {
  const [items, setItems] = useState<JobApplication[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const params = filterStatus && filterStatus !== 'all' ? `?status=${filterStatus}` : ''
      const r = await api.get<JobApplication[] | { items: JobApplication[] }>(`/recruitment/applications${params}`)
      const arr = Array.isArray(r) ? r : (r?.items ?? [])
      setItems(arr)
      setError(null)
    } catch (e: any) {
      setError(e?.message ?? 'load failed')
    } finally { setLoading(false) }
  }, [filterStatus])

  useEffect(() => { void refresh() }, [refresh])

  return { items, loading, error, refresh }
}

export async function getApplication(id: number): Promise<JobApplication> {
  return api.get<JobApplication>(`/recruitment/applications/${id}`)
}

export async function updateApplication(id: number, opts: {
  status?: string; internalNotes?: string; assignedToUserId?: string | null
}): Promise<JobApplication> {
  return api.put<JobApplication>(`/recruitment/applications/${id}`, opts)
}

// ── ÖFFENTLICH (kein Auth nötig) ──────────────────────────────────────────
export async function getPublicJob(code: string): Promise<JobLink> {
  return api.get<JobLink>(`/recruitment/public/${encodeURIComponent(code)}`, { noAuth: true })
}

export interface SubmitApplicationInput {
  code:        string
  firstName:   string
  lastName:    string
  email:       string
  phone?:      string
  coverLetter?: string
}

export async function submitApplication(input: SubmitApplicationInput): Promise<{ id: number }> {
  return api.post<{ id: number }>('/recruitment/public/submit', input, { noAuth: true })
}

/// Datei-Upload pro Bewerbung (multipart). Liefert die R2-URL zurück.
export async function uploadApplicationFile(applicationId: number, file: File): Promise<{ url: string; name: string; bytes: number }> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${API_ORIGIN}/api/v1/recruitment/public/${applicationId}/upload`, {
    method: 'POST',
    body: fd,
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`)
  return json
}
